import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AthenaCodeBlock } from './AthenaCodeBlock';
import { AthenaCallout, detectCalloutStyle } from './AthenaCallout';
import { getRenderer } from './presentationRegistry';
import { getReadingConfig, applyHighlights, parseDocumentTag } from '../utils/athenaSmartPresentation';
import { formatProseMarkdown } from '../utils/proseFormatter';
import { DocumentHeader } from './DocumentHeader';
import { usePresentationBreakpoint } from './presentation-system';
import './presentation/presentation.css';

// ── Visual constants ──────────────────────────────────────────────────────────
const C = {
  accent:   '#38bdf8',
  muted:    '#6b7aaa',
  border:   'rgba(255,255,255,0.08)',
  // Document hierarchy palette
  h1:  '#f0f4ff',   // near-white — title level
  h2:  '#b8ceff',   // soft indigo — section level
  h3:  '#8899cc',   // muted blue — subsection
};

// Correção tipográfica (conforto de leitura estilo ChatGPT) — pilha de fontes
// para texto de resposta. Inter já está carregada via Google Fonts em
// public/index.html; fallback de sistema garante leitura mesmo se a rede falhar.
const FONT_STACK = '"Inter", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
const MONO_STACK  = '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace';

function leadingText(children) {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    for (const c of children) {
      if (typeof c === 'string') return c;
    }
  }
  return '';
}

// Build ReactMarkdown component map — memoized by caller
function buildMdComponents(rc, isMobile) {
  const ps = rc.paragraphSpacing;
  const h1Size = isMobile ? 24 : 28;
  const h1Line = isMobile ? '30px' : '34px';
  const h2Size = isMobile ? 20 : 22;
  const h2Line = isMobile ? '27px' : '29px';
  const h3Size = isMobile ? 17 : 18;
  const h3Line = isMobile ? '24px' : '26px';

  return {
    // ── Document hierarchy ─────────────────────────────────────────────────────
    h1: ({ node, ...p }) => (
      <h1 style={{
        fontFamily: FONT_STACK,
        fontSize: `calc(${h1Size}px * var(--athena-font-scale, 1))`, lineHeight: h1Line,
        fontWeight: 700, color: C.h1,
        margin: '30px 0 12px', letterSpacing: '-0.025em',
      }} {...p} />
    ),
    h2: ({ node, ...p }) => (
      <h2 style={{
        fontFamily: FONT_STACK,
        fontSize: `calc(${h2Size}px * var(--athena-font-scale, 1))`, lineHeight: h2Line,
        fontWeight: 600, color: C.h2,
        margin: '28px 0 10px',
        paddingLeft: 9,
        borderLeft: '2px solid rgba(56,189,248,0.4)',
        letterSpacing: '-0.018em',
      }} {...p} />
    ),
    h3: ({ node, ...p }) => (
      <h3 style={{
        fontFamily: FONT_STACK,
        fontSize: `calc(${h3Size}px * var(--athena-font-scale, 1))`, lineHeight: h3Line,
        fontWeight: 600, color: C.h3,
        margin: '22px 0 8px',
      }} {...p} />
    ),

    // ── Block text with highlights ─────────────────────────────────────────────
    p: ({ node, children, ...props }) => {
      const cs = detectCalloutStyle(leadingText(children));
      if (cs) {
        return (
          <AthenaCallout color={cs.color} bg={cs.bg} border={cs.border}>
            {children}
          </AthenaCallout>
        );
      }
      return (
        <p style={{ margin: `0 0 ${ps}px` }} {...props}>
          {applyHighlights(children)}
        </p>
      );
    },
    ul: ({ node, ...p }) => (
      <ul style={{ paddingLeft: 22, margin: '10px 0 16px' }} {...p} />
    ),
    ol: ({ node, ...p }) => (
      <ol style={{ paddingLeft: 22, margin: '10px 0 16px' }} {...p} />
    ),
    li: ({ node, children, ...p }) => (
      <li style={{ marginBottom: 7 }} {...p}>{applyHighlights(children)}</li>
    ),

    // ── Code blocks ────────────────────────────────────────────────────────────
    pre: ({ node, children }) => <>{children}</>,
    code: ({ node, className, children, ...props }) => {
      const isBlock = Boolean(className) ||
        (typeof children === 'string' && children.includes('\n'));
      if (isBlock) return <AthenaCodeBlock className={className}>{children}</AthenaCodeBlock>;
      return (
        <code style={{
          background: 'rgba(255,255,255,.08)', borderRadius: 4,
          padding: '1px 5px', fontSize: 14, lineHeight: '21px', fontFamily: MONO_STACK,
        }}>
          {children}
        </code>
      );
    },

    // ── Tables ─────────────────────────────────────────────────────────────────
    table: ({ node, ...p }) => (
      <div style={{ overflowX: 'auto', margin: '8px 0' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }} {...p} />
      </div>
    ),
    thead: ({ node, ...p }) => <thead {...p} />,
    th: ({ node, ...p }) => (
      <th style={{
        border: '1px solid rgba(255,255,255,.15)', padding: '6px 10px',
        background: 'rgba(255,255,255,.06)', textAlign: 'left', fontWeight: 600,
      }} {...p} />
    ),
    td: ({ node, ...p }) => (
      <td style={{ border: '1px solid rgba(255,255,255,.1)', padding: '6px 10px' }} {...p} />
    ),

    // ── Inline ─────────────────────────────────────────────────────────────────
    strong: ({ node, ...p }) => <strong style={{ color: '#dde4ff', fontWeight: 600 }} {...p} />,
    em:     ({ node, ...p }) => <em style={{ color: C.muted }} {...p} />,
    a:      ({ node, ...p }) => (
      <a style={{ color: C.accent, textDecoration: 'underline' }}
        target="_blank" rel="noopener noreferrer" {...p} />
    ),
    blockquote: ({ node, children, ...props }) => (
      <blockquote style={{
        borderLeft: `3px solid ${C.accent}`, paddingLeft: 14,
        margin: '8px 0', color: C.muted, fontStyle: 'italic',
        fontSize: '15.5px', lineHeight: '23px',
      }} {...props}>
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '16px 0' }} />
    ),
  };
}

// ── Engine ────────────────────────────────────────────────────────────────────
export const AthenaPresentationEngine = React.memo(
  function AthenaPresentationEngine({ content, cognitiveProfile }) {
    const isMobile = usePresentationBreakpoint();
    const rc      = useMemo(() => getReadingConfig(cognitiveProfile, isMobile), [cognitiveProfile, isMobile]);
    const mdComps = useMemo(() => buildMdComponents(rc, isMobile), [rc, isMobile]);

    // Document tag: title + executive summary at the top of structured responses
    const { docHeader, cleanText } = useMemo(() => parseDocumentTag(content || ''), [content]);

    // Caso 1: só o fallback de texto puro reorganiza prosa excessiva em
    // parágrafos curtos / listas markdown. Texto curto ou já estruturado
    // (código, tabela, listas) passa inalterado. Calculado antes do early
    // return abaixo para não violar as Rules of Hooks.
    const displayText = useMemo(() => formatProseMarkdown(cleanText), [cleanText]);

    // Specialized whole-message renderers via registry (e.g. project view) —
    // detection runs on the original cleanText, untouched by prose formatting.
    // Sem max-width/fontFamily aqui de propósito: renderers especializados
    // definem sua própria tipografia e não devem herdar a largura de leitura.
    const entry = getRenderer(cleanText);
    if (entry) {
      const { Component, match } = entry;
      return (
        <>
          {docHeader && <DocumentHeader {...docHeader} />}
          <Component content={cleanText} matchData={match} />
        </>
      );
    }

    return (
      <div
        className="athena-prose"
        style={{
          fontFamily: FONT_STACK,
          fontSize: rc.fontSize,
          lineHeight: rc.lineHeight,
          letterSpacing: isMobile ? 'normal' : '-0.005em',
          width: '100%',
          maxWidth: 760,
        }}
      >
        {docHeader && <DocumentHeader {...docHeader} />}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComps}>
          {displayText}
        </ReactMarkdown>
      </div>
    );
  }
);
